// src/native/compliance/complianceStorage.js
//
// WS-7 COMPLIANCE. A tiny durable key/value layer for consent records.
//
// Three tiers, deliberately:
//
//   1. localStorage        available everywhere and synchronously, so the gate
//                          can decide what to render on the very first paint
//                          without a flash of the consent screen.
//   2. Capacitor Preferences  native only, written through UserDefaults. This is
//                          the copy that survives a webview data clear, which
//                          localStorage does not.
//   3. Supabase user_metadata  written by aiConsentStore once a user is signed
//                          in. Survives reinstall and follows the account.
//
// This module owns tiers 1 and 2 only. Tier 3 lives in aiConsentStore because it
// needs the auth session.

import { isNative, loadNative } from '@/lib/platform';

/** Synchronous read of the localStorage tier. Returns null on anything unexpected. */
export function readLocal(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Synchronous write of the localStorage tier. Never throws. */
export function writeLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Private mode, quota, or a locked-down webview. Not fatal: the Preferences
    // tier and the Supabase tier still carry the record.
    return false;
  }
}

/** Removes the localStorage tier. Never throws. */
export function clearLocal(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

/**
 * Reads the native Preferences tier. Resolves to null on web, on a missing
 * plugin, or on any error. Async by necessity: the plugin is loaded through
 * loadNative so no browser ever requests the chunk.
 */
export async function readNativePref(key) {
  const mod = await loadNative(() => import('@capacitor/preferences'));
  if (!mod?.Preferences) return null;
  try {
    const { value } = await mod.Preferences.get({ key });
    if (!value) return null;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/** Writes the native Preferences tier. Resolves false on web. Never throws. */
export async function writeNativePref(key, value) {
  const mod = await loadNative(() => import('@capacitor/preferences'));
  if (!mod?.Preferences) return false;
  try {
    await mod.Preferences.set({ key, value: JSON.stringify(value) });
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads a record from the fastest tier that has one, preferring whichever
 * carries the newer version number. On web this is just localStorage.
 *
 * Returns { record, source } where source is 'local' | 'native' | null.
 */
export async function readDurable({ localKey, prefsKey }) {
  const local = readLocal(localKey);
  if (!isNative()) return { record: local, source: local ? 'local' : null };

  const native = await readNativePref(prefsKey);
  if (!native) return { record: local, source: local ? 'local' : null };
  if (!local) return { record: native, source: 'native' };

  // Both present. The higher version wins, because a reinstall can leave an old
  // localStorage copy behind while Preferences carries the current answer.
  const localVersion = Number(local.version) || 0;
  const nativeVersion = Number(native.version) || 0;
  return nativeVersion >= localVersion
    ? { record: native, source: 'native' }
    : { record: local, source: 'local' };
}

/** Writes a record to every tier this module owns. Never throws. */
export async function writeDurable({ localKey, prefsKey }, record) {
  writeLocal(localKey, record);
  await writeNativePref(prefsKey, record);
}
